/**
 * Health Status Page
 * Displays health status of both DataLake and Seed gRPC servers
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as HealthyIcon,
  CheckCircle,
  Error as UnhealthyIcon,
  Warning as DegradedIcon,
  HelpOutline as UnknownIcon,
} from '@mui/icons-material';
import { healthCheck as dataLakeHealthCheck } from '../services/grpcWebService.ts';
import { healthCheck as seedHealthCheck } from '../services/seedGrpcService.ts';

interface DataLakeHealth {
  healthy: boolean;
  message: string;
  version?: string;
  timestamp?: number;
}

interface SeedHealth {
  status: number;
  message: string;
  uptimeSeconds?: number;
  loadedModels?: number;
  modelNamesList?: string[];
  detailsMap?: Array<[string, string]>;
}

const HealthStatus: React.FC = () => {
  const [dataLakeHealth, setDataLakeHealth] = useState<DataLakeHealth | null>(null);
  const [seedHealth, setSeedHealth] = useState<SeedHealth | null>(null);
  const [dataLakeError, setDataLakeError] = useState<string | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Health status enum mapping (from Seed server)
  const HealthStatusEnum = {
    UNKNOWN: 0,
    HEALTHY: 1,
    DEGRADED: 2,
    UNHEALTHY: 3,
  };

  const getStatusInfo = (status: number) => {
    switch (status) {
      case HealthStatusEnum.HEALTHY:
        return {
          label: 'HEALTHY',
          color: 'success' as const,
          icon: <HealthyIcon sx={{ fontSize: 40 }} />,
        };
      case HealthStatusEnum.DEGRADED:
        return {
          label: 'DEGRADED',
          color: 'warning' as const,
          icon: <DegradedIcon sx={{ fontSize: 40 }} />,
        };
      case HealthStatusEnum.UNHEALTHY:
        return {
          label: 'UNHEALTHY',
          color: 'error' as const,
          icon: <UnhealthyIcon sx={{ fontSize: 40 }} />,
        };
      default:
        return {
          label: 'UNKNOWN',
          color: 'default' as const,
          icon: <UnknownIcon sx={{ fontSize: 40 }} />,
        };
    }
  };

  // Convert DataLake's boolean healthy field to status enum
  const getDataLakeStatus = (healthy: boolean): number => {
    return healthy ? HealthStatusEnum.HEALTHY : HealthStatusEnum.UNHEALTHY;
  };

  const formatUptime = (seconds?: number): string => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours}h ${minutes}m ${secs}s`;
  };

  const checkHealth = async () => {
    setLoading(true);
    setDataLakeError(null);
    setSeedError(null);

    // Check DataLake server health
    try {
      const dataLakeResponse = await dataLakeHealthCheck();
      setDataLakeHealth(dataLakeResponse);
    } catch (err: any) {
      console.error('[HealthStatus] DataLake health check failed:', err);
      setDataLakeError(err.message || 'Cannot connect to DataLake server');
      setDataLakeHealth(null);
    }

    // Check Seed server health
    try {
      const seedResponse = await seedHealthCheck();
      setSeedHealth(seedResponse);
    } catch (err: any) {
      console.error('[HealthStatus] Seed health check failed:', err);
      setSeedError(err.message || 'Cannot connect to Seed server');
      setSeedHealth(null);
    }

    setLoading(false);
  };

  // Check health on mount
  useEffect(() => {
    checkHealth();
  }, []);

  return (
    <Box sx={{ p: 4 }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
              System Health Status
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor the health of DataLake and Seed gRPC servers
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={checkHealth}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Server Health Cards */}
      <Grid container spacing={3}>
        {/* DataLake Server Health */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                DataLake gRPC Server
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Port 50051 (via Envoy 8080)
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : dataLakeError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {dataLakeError}
                </Alert>
              ) : dataLakeHealth ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    {getStatusInfo(getDataLakeStatus(dataLakeHealth.healthy)).icon}
                    <Box>
                      <Chip
                        label={getStatusInfo(getDataLakeStatus(dataLakeHealth.healthy)).label}
                        color={getStatusInfo(getDataLakeStatus(dataLakeHealth.healthy)).color}
                        size="medium"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {dataLakeHealth.message}
                      </Typography>
                    </Box>
                  </Box>

                  {dataLakeHealth.version && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Version:</strong> {dataLakeHealth.version}
                      </Typography>
                    </Box>
                  )}

                  {dataLakeHealth.timestamp && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Timestamp:</strong> {new Date(dataLakeHealth.timestamp * 1000).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Seed Server Health */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Seed gRPC Server
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Port 50052 (via Envoy 8081)
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : seedError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {seedError}
                </Alert>
              ) : seedHealth ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    {getStatusInfo(seedHealth.status).icon}
                    <Box>
                      <Chip
                        label={getStatusInfo(seedHealth.status).label}
                        color={getStatusInfo(seedHealth.status).color}
                        size="medium"
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {seedHealth.message}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Uptime:</strong> {formatUptime(seedHealth.uptimeSeconds)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Loaded Models:</strong> {seedHealth.loadedModels || 0}
                    </Typography>
                  </Box>

                  {seedHealth.modelNamesList && seedHealth.modelNamesList.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Model Names:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {seedHealth.modelNamesList.map((modelName, index) => (
                          <Chip
                            key={index}
                            label={modelName}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {seedHealth.detailsMap && seedHealth.detailsMap.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Details:
                      </Typography>
                      <List dense>
                        {seedHealth.detailsMap.map(([key, value], index) => (
                          <ListItem key={index} disableGutters>
                            <ListItemText
                              primary={`${key}: ${value}`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">No data available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Overall System Status */}
      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Overall System Status
        </Typography>
        {!loading && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {!dataLakeError && !seedError && dataLakeHealth && seedHealth &&
             dataLakeHealth.healthy && seedHealth.status === HealthStatusEnum.HEALTHY ? (
              <>
                <CheckCircle sx={{ color: 'success.main', fontSize: 30 }} />
                <Typography variant="body1" color="success.main" sx={{ fontWeight: 600 }}>
                  All systems operational
                </Typography>
              </>
            ) : dataLakeError && seedError ? (
              <>
                <UnhealthyIcon sx={{ color: 'error.main', fontSize: 30 }} />
                <Typography variant="body1" color="error.main" sx={{ fontWeight: 600 }}>
                  All systems down - Please check server connections
                </Typography>
              </>
            ) : (
              <>
                <DegradedIcon sx={{ color: 'warning.main', fontSize: 30 }} />
                <Typography variant="body1" color="warning.main" sx={{ fontWeight: 600 }}>
                  Partial system degradation - Some services unavailable
                </Typography>
              </>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default HealthStatus;
